import React, { memo } from 'react';
import { Box, Flex, Text } from '@chakra-ui/react';

/**
 * @param {object} props
 * @param {{ player: 'X' | 'O', index: number }[]} props.moves
 * @param {boolean} [props.fillParent] — stretch to parent height, fit moves without scroll (desktop + board-aligned panel)
 */
function TicTacToeMoveHistory({ moves, fillParent = false }) {
  const lastIdx = moves.length - 1;

  const listScrollStyles = fillParent
    ? { overflowY: 'hidden' }
    : {
        overflowY: 'auto',
        maxH: { base: 'min(200px, 40vh)', md: '240px' },
        scrollbarWidth: 'thin',
        scrollbarColor: 'rgba(148, 163, 184, 0.45) transparent',
        '&::-webkit-scrollbar': { width: '5px' },
        '&::-webkit-scrollbar-track': { background: 'transparent' },
        '&::-webkit-scrollbar-thumb': {
          background: 'rgba(148, 163, 184, 0.4)',
          borderRadius: 'full',
        },
        '&::-webkit-scrollbar-thumb:hover': {
          background: 'rgba(100, 116, 139, 0.5)',
        },
      };

  return (
    <Box
      h={fillParent ? '100%' : 'auto'}
      minH={fillParent ? 0 : undefined}
      flex={fillParent ? 1 : undefined}
      display="flex"
      flexDirection="column"
      bg="linear-gradient(180deg, rgba(241, 245, 249, 0.9) 0%, rgba(248, 250, 252, 0.95) 100%)"
      borderWidth="1px"
      borderColor="gray.200"
      borderRadius="2xl"
      overflow="hidden"
      boxShadow="0 1px 3px rgba(15, 23, 42, 0.05), inset 0 1px 0 rgba(255,255,255,0.7)"
    >
      <Flex align="center" justify="flex-start" px={3} py={2} flexShrink={0} gap={2} flexWrap="wrap">
        <Text
          as="h2"
          fontSize="9px"
          fontWeight="800"
          color="gray.500"
          textTransform="uppercase"
          letterSpacing="0.12em"
          lineHeight={1}
        >
          Move log
        </Text>
        {moves.length > 0 ? (
          <Text fontSize="10px" color="gray.400" fontWeight="500" lineHeight={1}>
            {moves.length} move{moves.length === 1 ? '' : 's'}
          </Text>
        ) : null}
      </Flex>

      <Box
        px={2}
        pb={2}
        pt={0}
        flex={fillParent ? 1 : undefined}
        minH={fillParent ? 0 : undefined}
        display="flex"
        flexDirection="column"
        overflow={fillParent ? 'hidden' : undefined}
        sx={
          fillParent
            ? { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }
            : undefined
        }
      >
        <Box
          flex={fillParent ? 1 : undefined}
          minH={fillParent ? 0 : '72px'}
          display="flex"
          flexDirection="column"
          sx={listScrollStyles}
        >
          {moves.length === 0 ? (
            <Flex
              align="center"
              justify="center"
              flex={fillParent ? 1 : undefined}
              minH="56px"
              borderRadius="lg"
              borderWidth="1px"
              borderStyle="dashed"
              borderColor="gray.200"
              bg="rgba(255,255,255,0.5)"
              px={2}
            >
              <Text fontSize="xs" color="gray.400" fontWeight="500" textAlign="center" lineHeight="short">
                Moves appear as you play
              </Text>
            </Flex>
          ) : (
            <Box
              flex={fillParent ? 1 : undefined}
              minH={fillParent ? 0 : undefined}
              display="flex"
              flexDirection="column"
              borderRadius="lg"
              borderWidth="1px"
              borderColor="gray.200"
              bg="rgba(255,255,255,0.75)"
              overflow="hidden"
              boxShadow="0 1px 2px rgba(15, 23, 42, 0.03)"
            >
              {moves.map((m, i) => {
                const isLatest = i === lastIdx;
                return (
                  <Flex
                    key={`${i}-${m.index}`}
                    align="center"
                    px={2}
                    py={fillParent ? 0 : 1.5}
                    minH={fillParent ? '20px' : undefined}
                    flex={fillParent ? 1 : undefined}
                    gap={0}
                    borderBottomWidth={i < moves.length - 1 ? '1px' : 0}
                    borderColor="gray.100"
                    bg={
                      isLatest
                        ? 'rgba(13, 148, 136, 0.06)'
                        : i % 2 === 1
                          ? 'rgba(248, 250, 252, 0.8)'
                          : 'transparent'
                    }
                    transition="background 0.12s ease"
                    _hover={{
                      bg: isLatest ? 'rgba(13, 148, 136, 0.09)' : 'rgba(241, 245, 249, 0.9)',
                    }}
                  >
                    <Text
                      w="20px"
                      flexShrink={0}
                      fontSize="10px"
                      fontWeight="700"
                      color="gray.400"
                      fontVariantNumeric="tabular-nums"
                      lineHeight={1.2}
                    >
                      {i + 1}
                    </Text>
                    <Text
                      w="20px"
                      flexShrink={0}
                      textAlign="center"
                      fontSize="xs"
                      fontWeight="800"
                      color={m.player === 'X' ? 'cyan.700' : 'orange.700'}
                      letterSpacing="-0.03em"
                      lineHeight={1.2}
                    >
                      {m.player}
                    </Text>
                    <Text px={1} flexShrink={0} fontSize="9px" color="gray.300" fontWeight="600" lineHeight={1.2}>
                      ·
                    </Text>
                    <Text
                      flex={1}
                      fontSize="xs"
                      fontWeight="600"
                      color="gray.600"
                      fontVariantNumeric="tabular-nums"
                      letterSpacing="-0.01em"
                      lineHeight={1.2}
                      noOfLines={1}
                    >
                      Cell {m.index + 1}
                    </Text>
                  </Flex>
                );
              })}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}

export default memo(TicTacToeMoveHistory);
